import {
  CheckCircle,
  Error as ErrorIcon,
  Group,
  Schedule,
} from '@campoint/odi-ui-icons';
import {
  ContainerProps,
  Icon,
  TextProps,
  Tooltip,
  chakra,
  forwardRef,
  useDisclosure,
  usePrevious,
} from '@chakra-ui/react';
import { EmotionIcon } from '@emotion-icons/emotion-icon';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PostStatusEnum } from '../../../generated/feed';
import { FeedPostBrokenModal } from '../components/modal/FeedPostBrokenModal';
import { FeedPostRejectedModal } from '../components/modal/FeedPostRejectedModal';

const FeedPostWrapperStatusLabel = forwardRef<
  TextProps & { icon: EmotionIcon },
  'span'
>((props, ref) => {
  const { icon, children, ...spanProps } = props;
  return (
    <chakra.span
      display={'inline-flex'}
      alignItems={'center'}
      textStyle={'caption'}
      fontWeight={400}
      ref={ref}
      {...spanProps}
    >
      <chakra.span aria-hidden paddingEnd={'1'}>
        {<Icon as={icon} boxSize={'6'} />}
      </chakra.span>
      <chakra.span>{children}</chakra.span>
    </chakra.span>
  );
});
export const FeaturedPostLabel = () => {
  const { t } = useTranslation(['feed']);
  return (
    <Tooltip
      label={t('feed:label.FeaturedPostDescription')}
      width={'full'}
      p={2}
      bg={{ base: 'white', md: 'gray.50' }}
      color={'black'}
      borderRadius={'8px'}
      placement="bottom-start"
      closeDelay={500}
    >
      <FeedPostWrapperStatusLabel
        icon={Group}
        children={t('feed:label.FeaturedPost')}
        color={'indigo.500'}
      />
    </Tooltip>
  );
};
export const BrokenPostLabel: React.FC<{ onDeletePost: () => void }> = ({
  onDeletePost,
}) => {
  const { t } = useTranslation(['feed']);
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <FeedPostWrapperStatusLabel
        icon={ErrorIcon}
        children={t('feed:text.Fehlgeschlagen')}
        color={'red.500'}
        cursor={'pointer'}
        onClick={onOpen}
      />
      <FeedPostBrokenModal
        isOpen={isOpen}
        onClose={onClose}
        onDeletePost={onDeletePost}
      />
    </>
  );
};
export const JustPublishedPostLabel = () => {
  const { t } = useTranslation(['feed']);
  return (
    <FeedPostWrapperStatusLabel
      icon={CheckCircle}
      children={t('feed:text.LiveAufVISITX')}
      color={'lime.500'}
    />
  );
};

export const ScheduledPostLabel = () => {
  const { t } = useTranslation(['feed']);
  return (
    <FeedPostWrapperStatusLabel
      icon={Schedule}
      children={t('feed:label.Eingeplant')}
      color={'indigo.500'}
    />
  );
};
export const RejectedPostLabel: React.FC<{
  onDeletePost: () => void;
  rejectionReason?: string;
}> = ({ onDeletePost, rejectionReason }) => {
  const { t } = useTranslation(['feed']);
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <FeedPostWrapperStatusLabel
        icon={ErrorIcon}
        children={t('feed:text.Abgelehnt')}
        color={'red.500'}
        cursor={'pointer'}
        onClick={onOpen}
      />
      <FeedPostRejectedModal
        isOpen={isOpen}
        onClose={onClose}
        rejectionReason={rejectionReason}
        onDeletePost={onDeletePost}
      />
    </>
  );
};
export const useFeedPostWrapper = ({
  status,
  rejectionReason,
  isFeatured,
  isScheduled,
  onDeletePost,
}: {
  status?: PostStatusEnum;
  rejectionReason?: string;
  isFeatured?: boolean;
  isScheduled?: boolean;
  onDeletePost: () => void;
}) => {
  const previusStatus = usePrevious(status);

  const highlightColor: ContainerProps['borderColor'] | undefined =
    React.useMemo(() => {
      if (status === PostStatusEnum.Ok && isFeatured) {
        return 'indigo.500';
      }

      switch (status) {
        case PostStatusEnum.Broken:
        case PostStatusEnum.Rejected:
          return 'red.500';
        default:
          return undefined;
      }
    }, [status, isFeatured]);

  const isHighlighted = !!highlightColor;

  const labelElement = React.useMemo(() => {
    if (
      status === PostStatusEnum.Ok &&
      previusStatus === PostStatusEnum.Transcoding
    ) {
      return <JustPublishedPostLabel />;
    }

    if (status === PostStatusEnum.Ok && isFeatured) {
      return <FeaturedPostLabel />;
    }

    if (status === PostStatusEnum.Ok && isScheduled) {
      return <ScheduledPostLabel />;
    }

    switch (status) {
      case PostStatusEnum.Broken:
        return <BrokenPostLabel onDeletePost={onDeletePost} />;
      case PostStatusEnum.Rejected:
        return (
          <RejectedPostLabel
            onDeletePost={onDeletePost}
            rejectionReason={rejectionReason ?? undefined}
          />
        );
      default:
        return undefined;
    }
  }, [
    status,
    previusStatus,
    isFeatured,
    isScheduled,
    onDeletePost,
    rejectionReason,
  ]);

  return {
    highlightColor,
    isHighlighted,
    labelElement,
  };
};
